import api from "./api";
import { uploadService } from "./uploadService";

export interface CourseMaterial {
  _id?: string;
  title: string;
  description?: string;
  type: "document" | "video" | "image" | "link" | "text";
  fileUrl?: string;
  content?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CourseModule {
  _id?: string;
  name: string;
  description?: string;
  order?: number;
  materials: CourseMaterial[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CourseMaterialsResponse {
  success: boolean;
  message: string;
  modules: CourseModule[];
  materials: CourseMaterial[]; // For backward compatibility
}

export interface AddMaterialResponse {
  success: boolean;
  message: string;
  material: CourseMaterial;
}

export interface AddModuleResponse {
  success: boolean;
  message: string;
  module: CourseModule;
}

class CourseMaterialService {
  // Get all materials for a course (organized by modules)
  // For students, use isStudent=true to call the student-specific endpoint
  async getCourseMaterials(
    courseId: string,
    isStudent: boolean = false
  ): Promise<CourseModule[]> {
    try {
      const endpoint = isStudent
        ? `/students/courses/${courseId}/materials`
        : `/courses/${courseId}/materials`;

      const response = (await api.get(endpoint)) as CourseMaterialsResponse;

      // Return modules, fallback to legacy materials if no modules
      if (response.modules && response.modules.length > 0) {
        return response.modules;
      } else if (response.materials && response.materials.length > 0) {
        // Create a default module for legacy materials
        return [
          {
            _id: "default",
            name: "General",
            description: "Default module for existing materials",
            order: 0,
            materials: response.materials,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ];
      }
      return [];
    } catch (error) {
      console.error("Error fetching course materials:", error);
      return [];
    }
  }

  // Get all modules for a course
  async getCourseModules(courseId: string): Promise<CourseModule[]> {
    try {
      const response = (await api.get(`/courses/${courseId}/modules`)) as {
        success: boolean;
        modules: CourseModule[];
      };
      return response.modules || [];
    } catch (error) {
      console.error("Error fetching course modules:", error);
      return [];
    }
  }

  // Create a new module
  async createModule(
    courseId: string,
    moduleData: Omit<
      CourseModule,
      "_id" | "materials" | "createdAt" | "updatedAt"
    >
  ): Promise<CourseModule | null> {
    try {
      const response = (await api.post(
        `/courses/${courseId}/modules`,
        moduleData
      )) as AddModuleResponse;

      if (response.success) {
        return response.module;
      } else {
        throw new Error(response.message || "Failed to create module");
      }
    } catch (error) {
      console.error("Error creating module:", error);
      throw error;
    }
  }

  // Update a module
  async updateModule(
    courseId: string,
    moduleId: string,
    moduleData: Partial<
      Omit<CourseModule, "_id" | "materials" | "createdAt" | "updatedAt">
    >
  ): Promise<CourseModule | null> {
    try {
      const response = (await api.put(
        `/courses/${courseId}/modules/${moduleId}`,
        moduleData
      )) as AddModuleResponse;

      if (response.success) {
        return response.module;
      } else {
        throw new Error(response.message || "Failed to update module");
      }
    } catch (error) {
      console.error("Error updating module:", error);
      throw error;
    }
  }

  // Delete a module
  async deleteModule(courseId: string, moduleId: string): Promise<boolean> {
    try {
      const response = (await api.delete(
        `/courses/${courseId}/modules/${moduleId}`
      )) as { success: boolean };
      return response.success;
    } catch (error) {
      console.error("Error deleting module:", error);
      return false;
    }
  }

  // Add a new material to a module
  async addModuleMaterial(
    courseId: string,
    moduleId: string,
    moduleName: string,
    material: Omit<CourseMaterial, "_id" | "createdAt" | "updatedAt">,
    file?: File
  ): Promise<CourseMaterial | null> {
    try {
      let fileUrl = material.fileUrl;

      // If a file is provided, upload it first
      if (file) {
        // For regular courses, upload to course-resources/regular/{module-name}/
        const destination = `course-resources/regular/${moduleName
          .replace(/\s+/g, "-")
          .toLowerCase()}`;
        const uploadResponse = await uploadService.uploadFile(
          file,
          destination
        );
        if (uploadResponse.success && uploadResponse.data) {
          fileUrl = uploadResponse.data.url;
        } else {
          throw new Error(uploadResponse.message || "File upload failed");
        }
      }

      const materialData = {
        ...material,
        fileUrl,
      };

      const response = (await api.post(
        `/courses/${courseId}/modules/${moduleId}/materials`,
        materialData
      )) as AddMaterialResponse;

      if (response.success) {
        return response.material;
      } else {
        throw new Error(response.message || "Failed to add material");
      }
    } catch (error) {
      console.error("Error adding module material:", error);
      throw error;
    }
  }

  // Bulk upload materials to a module
  async bulkUploadMaterials(
    courseId: string,
    moduleId: string,
    moduleName: string,
    files: File[]
  ): Promise<{
    success: boolean;
    uploaded: number;
    failed: number;
    errors: string[];
  }> {
    const results = {
      success: true,
      uploaded: 0,
      failed: 0,
      errors: [] as string[],
    };

    for (const file of files) {
      try {
        // Determine material type based on file extension
        const extension = file.name.split(".").pop()?.toLowerCase();
        let materialType: CourseMaterial["type"] = "document";

        if (
          ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension || "")
        ) {
          materialType = "image";
        } else if (
          ["mp4", "avi", "mov", "wmv", "mkv"].includes(extension || "")
        ) {
          materialType = "video";
        }

        // Create material data
        const materialData: Omit<
          CourseMaterial,
          "_id" | "createdAt" | "updatedAt"
        > = {
          title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension from title
          description: `Uploaded file: ${file.name}`,
          type: materialType,
          fileUrl: "",
          content: "",
        };

        await this.addModuleMaterial(
          courseId,
          moduleId,
          moduleName,
          materialData,
          file
        );
        results.uploaded++;
      } catch (error) {
        results.failed++;
        results.errors.push(
          `${file.name}: ${
            error instanceof Error ? error.message : "Upload failed"
          }`
        );
      }
    }

    results.success = results.failed === 0;
    return results;
  }
  async updateModuleMaterial(
    courseId: string,
    moduleId: string,
    moduleName: string,
    materialId: string,
    material: Omit<CourseMaterial, "_id" | "createdAt" | "updatedAt">,
    file?: File
  ): Promise<CourseMaterial | null> {
    try {
      let fileUrl = material.fileUrl;

      // If a new file is provided, upload it first
      if (file) {
        // For regular courses, upload to course-resources/regular/{module-name}/
        const destination = `course-resources/regular/${moduleName
          .replace(/\s+/g, "-")
          .toLowerCase()}`;
        const uploadResponse = await uploadService.uploadFile(
          file,
          destination
        );
        if (uploadResponse.success && uploadResponse.data) {
          fileUrl = uploadResponse.data.url;
        } else {
          throw new Error(uploadResponse.message || "File upload failed");
        }
      }

      const materialData = {
        ...material,
        fileUrl,
      };

      const response = (await api.put(
        `/courses/${courseId}/modules/${moduleId}/materials/${materialId}`,
        materialData
      )) as AddMaterialResponse;

      if (response.success) {
        return response.material;
      } else {
        throw new Error(response.message || "Failed to update material");
      }
    } catch (error) {
      console.error("Error updating module material:", error);
      throw error;
    }
  }

  // Delete a material from a module
  async deleteModuleMaterial(
    courseId: string,
    moduleId: string,
    materialId: string
  ): Promise<boolean> {
    try {
      const response = (await api.delete(
        `/courses/${courseId}/modules/${moduleId}/materials/${materialId}`
      )) as { success: boolean };
      return response.success;
    } catch (error) {
      console.error("Error deleting module material:", error);
      return false;
    }
  }

  // Add a new material to a course
  async addCourseMaterial(
    courseId: string,
    material: Omit<CourseMaterial, "_id" | "createdAt" | "updatedAt">,
    file?: File
  ): Promise<CourseMaterial | null> {
    try {
      let fileUrl = material.fileUrl;

      // If a file is provided, upload it first
      if (file) {
        const uploadResponse = await uploadService.uploadFile(file);
        if (uploadResponse.success && uploadResponse.data) {
          fileUrl = uploadResponse.data.url;
        } else {
          throw new Error(uploadResponse.message || "File upload failed");
        }
      }

      const materialData = {
        ...material,
        fileUrl,
      };

      const response = (await api.post(
        `/courses/${courseId}/materials`,
        materialData
      )) as AddMaterialResponse;

      // Note: api interceptor returns response.data directly, so response is the actual data
      if (response?.success) {
        return response.material;
      } else {
        throw new Error(response?.message || "Failed to add material");
      }
    } catch (error) {
      console.error("Error adding course material:", error);
      throw error;
    }
  }

  // Update an existing material
  async updateCourseMaterial(
    courseId: string,
    materialId: string,
    material: Omit<CourseMaterial, "_id" | "createdAt" | "updatedAt">,
    file?: File
  ): Promise<CourseMaterial | null> {
    try {
      let fileUrl = material.fileUrl;

      // If a new file is provided, upload it first
      if (file) {
        const uploadResponse = await uploadService.uploadFile(file);
        if (uploadResponse.success && uploadResponse.data) {
          fileUrl = uploadResponse.data.url;
        } else {
          throw new Error(uploadResponse.message || "File upload failed");
        }
      }

      const materialData = {
        ...material,
        fileUrl,
      };

      const response = (await api.put(
        `/courses/${courseId}/materials/${materialId}`,
        materialData
      )) as AddMaterialResponse;

      if (response.success) {
        return response.material;
      } else {
        throw new Error(response.message || "Failed to update material");
      }
    } catch (error) {
      console.error("Error updating course material:", error);
      throw error;
    }
  }

  // Delete a material from a course
  async deleteCourseMaterial(
    courseId: string,
    materialId: string
  ): Promise<boolean> {
    try {
      const response = (await api.delete(
        `/courses/${courseId}/materials/${materialId}`
      )) as { success: boolean };
      return response.success;
    } catch (error) {
      console.error("Error deleting course material:", error);
      return false;
    }
  }

  // Upload a file for a material
  async uploadMaterialFile(file: File): Promise<{
    success: boolean;
    fileUrl?: string;
    fileName?: string;
    message?: string;
  }> {
    try {
      const uploadResponse = await uploadService.uploadFile(file);
      if (uploadResponse.success && uploadResponse.data) {
        return {
          success: true,
          fileUrl: uploadResponse.data.url,
          fileName: uploadResponse.data.filename,
        };
      } else {
        return {
          success: false,
          message: uploadResponse.message || "Upload failed",
        };
      }
    } catch (error) {
      console.error("Error uploading material file:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Upload failed",
      };
    }
  }

  // Get material types for UI
  getMaterialTypes() {
    return [
      {
        value: "document",
        label: "Document",
        accept: ".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx",
      },
      { value: "video", label: "Video", accept: ".mp4,.avi,.mov,.wmv" },
      { value: "image", label: "Image", accept: ".jpg,.jpeg,.png,.gif,.bmp" },
      { value: "link", label: "Link", accept: "" },
      { value: "text", label: "Text", accept: "" },
    ];
  }

  // Validate file type based on material type
  validateFileType(file: File, materialType: string): boolean {
    const typeConfig = this.getMaterialTypes().find(
      (t) => t.value === materialType
    );
    if (!typeConfig || !typeConfig.accept) return true;

    const acceptedExtensions = typeConfig.accept
      .split(",")
      .map((ext) => ext.trim().toLowerCase());
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

    return acceptedExtensions.includes(fileExtension);
  }
}

export const courseMaterialService = new CourseMaterialService();
